import React from "react";

// Material
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";

// Assets
import sharingPicture from "../../assets/images/sharing-flow.svg";

// Components
import Section from "../../components/Section/Section";
import HighlightColor from "../../components/HighlightColor/index";
import colors from "../../config/colors";
import Text, { types } from "../../components/Text/Text";
import LinkScroll from "../../components/LinkScroll/LinkScroll";
import urls from "../../config/urls";

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: colors.lightGrey,
    paddingTop: "130px",
    paddingBottom: "145px",
    [theme.breakpoints.down("sm")]: {
      paddingTop: "32px",
      paddingBottom: "32px"
    }
  },
  container: {
    display: "flex",
    flexDirection: "row",
    maxWidth: "73vw",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexDirection: "column-reverse",
      alignItems: "center"
    }
  },
  content: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    [theme.breakpoints.down("sm")]: {
      alignItems: "center"
    }
  },
  breadcrumbs: {
    marginBottom: theme.spacing(3),
    color: colors.doveGray,
    fontFamily: ["Montserrat", "sans-serif"].join(","),

    fontSize: "20px",
    [theme.breakpoints.down("md")]: {
      fontSize: "18px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: "14px",
      marginBottom: theme.spacing(2)
    },
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  barContainer: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down(1440)]: {
      marginBottom: theme.spacing(2)
    }
  },
  barContent: {
    borderRadius: "62px",
    height: "22px",
    width: "420px",
    background:
      "transparent linear-gradient(90deg, #3ACCE1 0%, #25e7bd 100%) 0% 0% no-repeat padding-box",
    [theme.breakpoints.down("md")]: {
      height: "16px"
    },
    [theme.breakpoints.down(1440)]: {
      height: "14px"
    },
    [theme.breakpoints.down("sm")]: {
      display: "none"
    }
  },
  miniTitle: {
    marginBottom: theme.spacing(3),
    maxWidth: "17ch",
    fontSize: "56px",
    [theme.breakpoints.down("md")]: {
      fontSize: "48px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: "30px",
      marginBottom: theme.spacing(2)
    },
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      margin: "0 auto",
      marginBottom: theme.spacing(2)
    }
  },
  textContent: {
    fontFamily: ["Montserrat", "sans-serif"].join(","),

    fontWeight: 400,
    lineHeight: "1.5",
    marginBottom: theme.spacing(7),
    fontSize: "28px",
    width: "39ch",
    [theme.breakpoints.down("md")]: {
      fontSize: "22px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: "18px",
      marginBottom: theme.spacing(4)
    },
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
      width: "100%",
      maxWidth: "580px"
    }
  },
  button: {
    fontFamily: "Montserrat Black",
    textTransform: "none",
    fontSize: "24px",
    padding: "28px 124px",
    borderRadius: "10px",
    boxShadow: "0px 3px 66px #00000029",
    color: "#FFF",
    [theme.breakpoints.down("md")]: {
      fontSize: "18px",
      padding: "24px 90px"
    },
    [theme.breakpoints.down(1440)]: {
      fontSize: "14px",
      marginBottom: theme.spacing(4),
      padding: "24px 70px"
    },
    [theme.breakpoints.down("sm")]: {
      fontSize: "14px",
      marginBottom: theme.spacing(4),
      padding: "24px 0",
      width: "100%"
    }
  },
  mainImage: {
    width: "618px",
    marginLeft: "100px",
    paddingTop: "10%",
    [theme.breakpoints.down("md")]: {
      width: "540px"
    },
    [theme.breakpoints.down(1440)]: {
      transform: "none",
      width: "406px"
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: "0",
      paddingTop: "0",
      width: "100%",
      maxWidth: "430px"
    }
  },
  kulpa: {
    fontWeight: "800"
  }
}));

const SharingSection = () => {
  const classes = useStyles();

  return (
    <Section className={classes.root}>
      <div className={classes.container}>
        <div className={classes.content}>
          <Typography className={classes.breadcrumbs}>
            {`Upload > Share > Get Justice`}
          </Typography>
          <div className={classes.barContainer}>
            <div className={classes.barContent} />
          </div>
          <Text
            type={types.miniSectionTitle}
            className={classes.miniTitle}
            component="h2"
          >
            Sharing evidence with{" "}
            <HighlightColor className={classes.kulpa}>kulpa</HighlightColor>{" "}
            gives it purpose
          </Text>
          <div className={classes.text}>
            <Typography className={classes.textContent}>
              Our technology, certified processes, metadata capture, and
              patented features ensure that evidence uploaded to and shared
              through Kulpa is verified, authenticated and legally admissible.
              When you choose to share your files, the platform will
              automatically bundle your evidence into a package that ensures it
              can be immediately used in a legal process.
            </Typography>
          </div>
          <Button
            className={classes.button}
            color="secondary"
            variant="contained"
            disableElevation
            component={LinkScroll}
            to={`${urls.getConnected}#pageStart`}
          >
            See the results
          </Button>
        </div>
        <div className={classes.imageWrapper}>
          <img
            src={sharingPicture}
            className={classes.mainImage}
            alt="kulpa app securely storing legally admissible evidence"
          />
        </div>
      </div>
    </Section>
  );
};

export default SharingSection;
